import React from "react";
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cn } from "~/lib/cn";

type TableProps = HTMLAttributes<HTMLTableElement>;

/** Styled HTML table with Header, Body, Row, Head and Cell parts. */
export const Table = ({ className, ...props }: TableProps) => {
  return (
    <div className="relative w-full overflow-auto rounded-lg border">
      <table
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
};

type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableHeader = ({ className, ...props }: TableHeaderProps) => {
  return <thead className={cn("[&_tr]:border-b", className)} {...props} />;
};

type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableBody = ({ className, ...props }: TableBodyProps) => {
  return (
    <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  );
};

type TableFooterProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableFooter = ({ className, ...props }: TableFooterProps) => {
  return (
    <tfoot
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
};

type TableRowProps = HTMLAttributes<HTMLTableRowElement>;

export const TableRow = ({ className, ...props }: TableRowProps) => {
  return (
    <tr
      className={cn(
        "table-row border-b bg-white transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
};

type TableHeadProps = ThHTMLAttributes<HTMLTableCellElement>;

export const TableHead = ({ className, ...props }: TableHeadProps) => {
  return (
    <th
      className={cn(
        "table-cell h-12 bg-gray-25 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
};

type TableCellProps = TdHTMLAttributes<HTMLTableCellElement>;

export const TableCell = ({ className, ...props }: TableCellProps) => {
  return (
    <td
      className={cn(
        "table-cell p-4 align-middle [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
};

type TableCaptionProps = HTMLAttributes<HTMLTableCaptionElement>;

export const TableCaption = ({ className, ...props }: TableCaptionProps) => {
  return (
    <caption
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
};
